<script>
import Swal from 'sweetalert2';
import { mapGetters } from 'vuex';
import { FormUtils } from '../../utils/FormUtils';
import InvoicesService from '../../services/InvoicesService';
import ClientService from '../../services/ClientService';
import { ErrorResponse } from '../../models/ErrorResponse';

export default {
  name: 'pos-step-invoice',
  props: {
    invoiceRequired: {
      type: String,
      default: 'no',
    },
  },
  data: () => ({
    invoiceSwitchValue: false,
    showNominativeModal: false,
    loadingInvoiceClients: false,
    pendingInvoiceClientRequest: null,
    invoiceClientSearchTimeout: null,
    invoiceClientSearchDebounceDelay: 500,
    invoiceClientSearchMinLength: 2,
    invoiceClients: [],
    invoiceClientOptions: [],
    invoiceClientSearch: '',
    invoiceClientSelected: null,
    clientName: '',
    clientRfc: '',
    clientCp: '',
    clientRegime: null,
    clientUsoCfdi: null,
    clientPaymentMethod: null,
    clientPaymentType: null,
    clientRegimeId: null,
    fiscalRegimeSearch: '',
    loadingFiscalRegimes: false,
    lockFiscalRegime: false,
    loadingUsoCfdi: false,
    loadingFiscal: false,
    billToSelectedClientRequestId: 0,
    billToSelectedClient: 'no',
    isResolvingInvoiceToggle: false,
    rfcRules: [
      (v) => !v || v.length >= 12 || 'RFC mínimo 12 caracteres',
      (v) => !v || v.length <= 13 || 'RFC máximo 13 caracteres',
      (v) => !v || /^[A-Z0-9]+$/.test(v) || 'RFC solo letras y números',
    ],
    cpRules: [
      (v) => !v || /^\d{5}$/.test(v) || 'CP debe tener 5 dígitos',
    ],
    formaPagos: [],
    metodoPagos: [],
  }),
  watch: {
    async billToSelectedClient(val) {
      if (this.invoiceRequired !== 'yes') return;
      const requestId = this.billToSelectedClientRequestId + 1;
      this.billToSelectedClientRequestId = requestId;

      if (val === 'no') {
        this.loadingFiscal = false;
        this.clearNominativeClientFields();
        return;
      }

      if (!this.selectedClient || !this.selectedClient.id) {
        this.loadingFiscal = false;
        return;
      }

      if (this.selectedClient && this.selectedClient.id) {
        await this.fillNominativeFromSelectedClient(requestId);
      }
    },
  },
  computed: {
    ...mapGetters({
      selectedClient: 'selectedClient',
      realPaidCash: 'realPaidCash',
      payments: 'payments',
      paymentInvoiceMisc: 'paymentInvoiceMisc',
      paymentInvoiceClients: 'paymentInvoiceClients',
      paymentFiscalRegimes: 'paymentFiscalRegimes',
      paymentUsoCfdiItems: 'paymentUsoCfdiItems',
      defaultFiscalRegimes: 'defaultFiscalRegimes',
      defaultUsoCfdiItems: 'defaultUsoCfdiItems',
    }),
    fiscalRegimes() {
      if (Array.isArray(this.paymentFiscalRegimes)) {
        if (!this.invoiceClientSelected && !this.isValidRfc(this.clientRfc)) {
          return this.defaultFiscalRegimes.map((x) => ({
            label: `[${x.code}] ${x.name}`,
            value: String(x.code),
          }));
        }
        return this.paymentFiscalRegimes.map((x) => ({
          label: `[${x.code}] ${x.name}`,
          value: String(x.code),
        }));
      }
      return [];
    },
    usoCfdiItems() {
      if (Array.isArray(this.paymentUsoCfdiItems)) {
        if (!this.clientRegimeId) {
          return this.defaultUsoCfdiItems.map((x) => ({
            ...x,
            code: String(x.code ?? ''),
            name: String(x.name ?? ''),
            display: `${x.code} - ${x.name}`,
          }));
        }
        return this.paymentUsoCfdiItems.map((x) => ({
          ...x,
          code: String(x.code ?? ''),
          name: String(x.name ?? ''),
          display: `${x.code} - ${x.name}`,
        }));
      }
      return [];
    },
    lockSatPaymentFields() {
      return this.isSatCutoffCase();
    },
  },
  methods: {
    onlyNumberKeypress(e) {
      return FormUtils.onlyNumberKeypress(e);
    },
    onCpPaste(e) {
      const text = (e.clipboardData || window.clipboardData).getData('text') || '';
      this.clientCp = text.replace(/\D/g, '').slice(0, 5);
    },
    isSatCutoffCase() {
      const baseDate = new Date();
      const isSaturday = baseDate.getDay() === 6;
      const monday = new Date(baseDate);
      monday.setDate(baseDate.getDate() + 2);
      const mondayIsNextMonth = monday.getMonth() !== baseDate.getMonth();
      const hasCardPayment = this.payments.some(
        (p) => Number(p.paymentMethod) === 2 || Number(p.paymentMethod) === 3,
      );
      return this.invoiceRequired === 'yes' && hasCardPayment && isSaturday && mondayIsNextMonth;
    },
    clearInvoiceClientFields() {
      this.clientName = '';
      this.clientRfc = '';
      this.clientCp = '';
      this.clientRegimeId = null;
      this.clientUsoCfdi = null;
      this.clientPaymentMethod = null;
      this.clientPaymentType = null;
      this.invoiceClientSelected = null;
      this.invoiceClientSearch = '';
    },
    async searchInvoiceClient(query) {
      if (this.pendingInvoiceClientRequest) {
        this.pendingInvoiceClientRequest.cancel();
        this.pendingInvoiceClientRequest = null;
      }

      this.invoiceClientSearchTimeout = setTimeout(async () => {
        await this.performInvoiceClientSearch(query);
      }, this.invoiceClientSearchDebounceDelay);
      this.pendingInvoiceClientRequest = {
        cancel: () => clearTimeout(this.invoiceClientSearchTimeout),
      };
    },
    async applyClientFiscalRegime(item) {
      const raw = item?.fiscalRegimeCode
        ?? item?.fiscal_regime_code
        ?? item?.fiscalRegimeId
        ?? item?.regimeId
        ?? null;
      if (raw === null) {
        return;
      }
      const rawStr = String(raw).trim();
      const catalog = await ClientService.fiscalRegimeCatalog('');
      const found = catalog.find((r) => String(r.value) === rawStr);
      if (found) {
        const extractedCode = found.label?.match(/^\[(\d+)\]/)?.[1] || null;
        this.clientRegimeId = extractedCode;
      }
    },
    isLatestBillToSelectedClientRequest(requestId) {
      return requestId === this.billToSelectedClientRequestId;
    },
    async fillNominativeFromSelectedClient(requestId = this.billToSelectedClientRequestId) {
      const clientId = this.selectedClient?.id;
      if (!clientId || !this.isLatestBillToSelectedClientRequest(requestId)) return;

      try {
        this.loadingFiscal = true;
        if (!this.invoiceClients?.length) await this.preloadInvoiceClients();
        if (!this.isLatestBillToSelectedClientRequest(requestId)) return;

        if (!this.paymentInvoiceMisc.cfdi_use?.length || !this.paymentInvoiceMisc.payment_type?.length || !this.paymentInvoiceMisc.payment_method?.length) {
          await this.preloadInvoiceMisc();
          if (!this.isLatestBillToSelectedClientRequest(requestId)) return;
        }

        const item = this.invoiceClients.find((c) => {
          const anyId = c.id ?? c.clientId ?? c.value ?? c.customerId ?? c.personId;
          return Number(anyId) === Number(clientId);
        });
        if (!item || !this.isLatestBillToSelectedClientRequest(requestId)) return;

        this.clientCp = item.zipCode ?? item.zip_code ?? item.cp ?? item.postalCode ?? null;
        this.clientName = (
          item.name ?? item.razonSocial ?? item.businessName ?? item.fullName ?? ''
        ).trim() || null;
        this.clientRfc = (
          item.rfc ?? item.taxId ?? item.tax_id ?? item.code ?? ''
        ).trim() || null;

        if (!this.isLatestBillToSelectedClientRequest(requestId)) return;

        this.applyClientFiscalRegime(item);
        if (!this.isLatestBillToSelectedClientRequest(requestId)) return;

        if (!this.lockFiscalRegime && this.isValidRfc(this.clientRfc)) {
          if (!this.isLatestBillToSelectedClientRequest(requestId)) return;
        }

        if (!this.isLatestBillToSelectedClientRequest(requestId)) return;

        this.applyDefaultPaymentConditions();

        if (!this.isLatestBillToSelectedClientRequest(requestId)) return;
        await this.$nextTick();
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
      } finally {
        if (this.isLatestBillToSelectedClientRequest(requestId)) {
          this.loadingFiscal = false;
        }
      }
    },
    async onInvoiceClientChange(selected) {
      if (!selected) return;
      this.clientName = selected.name || '';
      this.clientRfc = (selected.rfc || '').toUpperCase();
      this.clientCp = selected.zipCode || '';
      this.clientRegimeId = this.fiscalRegimes.find((regime) => regime.value === selected.fiscalRegimeId).value || null;
    },
    isValidRfc(rfc) {
      const clean = String(rfc || '').replace(/[^A-Za-z0-9]/g, '').toUpperCase();
      const isValid = clean.length === 12 || clean.length === 13;
      return isValid;
    },
    normalizePaymentCatalogItems(arr, codeKeys = ['code', 'value', 'clave', 'key'], labelKeys = ['label', 'name', 'descripcion', 'description']) {
      return (arr || []).map((x) => {
        const code = codeKeys.map((k) => x?.[k]).find((v) => v !== undefined && v !== null && v !== '');
        const label = labelKeys.map((k) => x?.[k]).find((v) => v !== undefined && v !== null && v !== '');
        return {
          ...x,
          code: String(code ?? '').trim(),
          label: String(label ?? '').trim(),
          display: `${String(code ?? '').trim()} - ${String(label ?? '').trim()}`,
        };
      });
    },
    getMissingPaymentMetadata() {
      const missing = [];
      if (!this.paymentInvoiceMisc) missing.push('miscelánea de facturación');
      if (!Array.isArray(this.paymentInvoiceClients)) missing.push('clientes de facturación');
      if (!Array.isArray(this.paymentFiscalRegimes)) missing.push('regímenes fiscales');
      if (!Array.isArray(this.paymentUsoCfdiItems)) missing.push('usos CFDI');
      return missing;
    },
    async ensurePaymentMetadata(showWarning = true) {
      await this.$store.dispatch('preloadPaymentMetadata');
      this.hydratePaymentMetadataFromStore();

      const missing = this.getMissingPaymentMetadata();
      if (missing.length > 0) {
        if (showWarning) {
          Swal.fire({
            title: 'No se pudo habilitar la facturación',
            html: `No fue posible cargar:<br><br>${missing.map((item) => `• ${item}`).join('<br>')}<br><br>La venta puede continuar sin factura.`,
            icon: 'warning',
            confirmButtonText: 'Entendido',
          });
        }
        return false;
      }
      return true;
    },
    hydratePaymentMetadataFromStore() {
      if (!this.paymentInvoiceMisc) {
        return;
      }

      this.formaPagos = this.normalizePaymentCatalogItems(this.paymentInvoiceMisc.payment_type);
      this.metodoPagos = this.normalizePaymentCatalogItems(this.paymentInvoiceMisc.payment_method);

      if (Array.isArray(this.paymentInvoiceClients)) {
        this.invoiceClients = this.paymentInvoiceClients;
      }
    },
    async preloadInvoiceMisc() {
      if (!this.paymentInvoiceMisc) {
        await this.$store.dispatch('preloadPaymentMetadata');
      }
      this.hydratePaymentMetadataFromStore();
      return !!this.paymentInvoiceMisc;
    },
    async preloadInvoiceClients() {
      if (!this.paymentInvoiceClients) {
        await this.$store.dispatch('preloadPaymentMetadata');
      }
      this.hydratePaymentMetadataFromStore();
      return Array.isArray(this.paymentInvoiceClients);
    },
    async preloadCatalogs() {
      if (!this.paymentInvoiceMisc.cfdi_use?.length || !this.paymentInvoiceMisc.payment_type?.length || !this.paymentInvoiceMisc.payment_method?.length) {
        await this.preloadInvoiceMisc();
      }
      this.applyDefaultPaymentConditions();
      if (!this.invoiceClients?.length) {
        await this.preloadInvoiceClients();
      }
      this.billToSelectedClient = 'no';
      this.clearNominativeClientFields();
    },
    satFormaPagoCodeFromPayments() {
      const totalsByMethod = {};
      this.payments.forEach((p) => {
        const pm = p.paymentMethod;
        const amt = Number(p.totalImport || 0);
        totalsByMethod[pm] = (totalsByMethod[pm] || 0) + amt;
      });
      if (this.realPaidCash && this.realPaidCash > 0) {
        totalsByMethod[1] = (totalsByMethod[1] || 0) + Number(this.realPaidCash);
      }
      const entries = Object.entries(totalsByMethod);
      if (entries.length === 0) return null;
      entries.sort((a, b) => Number(b[1]) - Number(a[1]));
      const pm = Number(entries[0][0]);
      if (pm === 1) return '01';
      if (pm === 4) return '02';
      if (pm === 5) return '03';
      if (pm === 2) return '28';
      if (pm === 3) return '04';
      return null;
    },
    satMetodoPagoCode() {
      return 'PUE';
    },
    findBySatCode(arr, satCode) {
      const norm = (v) => String(v ?? '').trim().toUpperCase();
      const target = norm(satCode);
      if (!target) return null;
      return (arr || []).find((x) => {
        const candidates = [x.value, x.code, x.clave, x.key, x.id, x.satCode, x.sat_code].map(norm);
        return candidates.includes(target);
      }) || null;
    },
    findBySatCodePadded(arr, satCode) {
      const norm = (v) => String(v ?? '').trim().toUpperCase();
      const target = norm(satCode);
      if (!target) return null;
      const alt = target.length === 1 ? `0${target}` : target;
      const alt2 = target.length === 2 && target.startsWith('0') ? String(parseInt(target, 10)) : target;
      return this.findBySatCode(arr, target)
        || this.findBySatCode(arr, alt)
        || this.findBySatCode(arr, alt2)
        || null;
    },
    applyDefaultPaymentConditions() {
      const formaCode = this.satFormaPagoCodeFromPayments();
      const metodoCode = this.satMetodoPagoCode() || 'PUE';
      const isCutoffCase = this.isSatCutoffCase();
      if (isCutoffCase) {
        this.clientPaymentType = this.findBySatCodePadded(this.metodoPagos, '99');
        this.clientPaymentMethod = this.findBySatCode(this.formaPagos, 'PPD');
      } else {
        this.clientPaymentMethod = metodoCode
          ? this.findBySatCode(this.paymentInvoiceMisc.payment_type, metodoCode)
          : null;
        this.clientPaymentType = formaCode
          ? this.findBySatCodePadded(this.paymentInvoiceMisc.payment_method, formaCode)
          : null;
      }
    },
    onClientRfcChange() {
      if (!this.isValidRfc(this.clientRfc)) {
        return;
      }
      this.$store.dispatch('fetchFiscalRegimes', this.clientRfc);
    },
    async performInvoiceClientSearch(query) {
      this.invoiceClientSearch = (query || '').trim();
      if (this.invoiceClientSearch.length < this.invoiceClientSearchMinLength) {
        this.invoiceClientOptions = [];
        return;
      }
      try {
        this.loadingInvoiceClients = true;
        this.invoiceClientOptions = await InvoicesService.getInvoiceClientsAutocomplete(
          this.invoiceClientSearch,
          20,
        );
      } catch (e) {
        console.error(e);
        this.invoiceClientOptions = [];
      } finally {
        this.loadingInvoiceClients = false;
      }
    },
    resetInvoiceState() {
      this.billToSelectedClient = 'no';
      this.clientPaymentMethod = null;
      this.clientPaymentType = null;
      this.lockFiscalRegime = false;
      this.clearNominativeClientFields();
    },
    clearNominativeClientFields() {
      this.clientName = '';
      this.clientRfc = '';
      this.clientCp = '';
      this.clientRegimeId = null;
      this.clientUsoCfdi = null;
      this.lockFiscalRegime = false;
      this.invoiceClientOptions = [];
      this.invoiceClientSelected = null;
      this.invoiceClientSearch = '';
    },
    validateInvoiceForm() {
      if (this.invoiceRequired !== 'yes') return true;
      const rfc = String(this.clientRfc || '').trim().toUpperCase();
      const cp = String(this.clientCp || '').trim();
      if (!this.clientName?.trim()) return 'Ingresa la razón social';
      if (!(rfc.length === 12 || rfc.length === 13)) return 'El RFC debe tener 12 o 13 caracteres';
      if (!/^\d{5}$/.test(cp)) return 'CP inválido (5 dígitos)';
      if (!this.clientRegimeId) return 'Régimen fiscal requerido';
      if (!this.clientUsoCfdi) return 'Uso CFDI requerido';
      if (!this.clientPaymentMethod) return 'Forma de pago requerida';
      if (!this.clientPaymentType) return 'Método de pago requerido';
      return true;
    },
    sanitizeConcepts(factura) {
      if (!Array.isArray(factura?.concepts)) return factura;
      const sanitizedConcepts = factura.concepts.map((c) => {
        const desc = String(c?.name || c?.description || '').trim();
        return {
          ...c,
          name: desc || 'PRODUCTO',
          description: desc || 'PRODUCTO',
        };
      });
      return {
        ...factura,
        concepts: sanitizedConcepts,
      };
    },
    async buildFacturaFromSale(purchaseOrderId) {
      const factura = await InvoicesService.getFacturaSale(purchaseOrderId, 1);
      if (factura instanceof ErrorResponse) {
        throw factura;
      }
      factura.client = {
        code: String(this.clientRfc || '').trim().toUpperCase(),
        name: String(this.clientName || '').trim(),
        zip_code: String(this.clientCp || '').trim(),
        fiscal_regime_code: String(this.clientRegimeId || '').trim(),
      };
      factura.cfdi_use = {
        code: this.clientUsoCfdi?.code || this.clientUsoCfdi?.value,
        name: this.clientUsoCfdi?.name || this.clientUsoCfdi?.label || '',
      };
      factura.payment_type = {
        code: this.clientPaymentMethod?.code || this.clientPaymentMethod?.value,
        name: this.clientPaymentMethod?.name || this.clientPaymentMethod?.label || '',
      };
      factura.payment_method = {
        code: this.clientPaymentType?.code || this.clientPaymentType?.value,
        name: this.clientPaymentType?.name || this.clientPaymentType?.label || '',
      };
      return this.sanitizeConcepts(factura);
    },
  },
};
</script>

<template>
<div class="mt-2">
  <div class="d-flex align-center justify-space-between mb-1">
    <div class="text-subtitle-2">
      Facturar a nombre del cliente
    </div>
    <v-btn
      small
      outlined
      color="primary"
      class="px-2"
      @click="clearInvoiceClientFields"
    >
      Limpiar campos
    </v-btn>
  </div>
  <v-radio-group
    v-model="billToSelectedClient"
    row
    class="pa-0 ma-0"
    :disabled="selectedClient === null"
  >
    <v-radio color="secondary" label="Sí" value="yes" />
    <v-radio color="secondary" label="No" value="no" />
  </v-radio-group>
  <v-autocomplete
    v-model="invoiceClientSelected"
    :items="invoiceClientOptions"
    :search-input.sync="invoiceClientSearch"
    :loading="loadingInvoiceClients"
    item-text="label"
    return-object
    no-filter
    outlined
    dense
    clearable
    hide-no-data
    label="Buscar por Razón Social / RFC"
    @update:search-input="searchInvoiceClient"
    @change="onInvoiceClientChange"
  />
  <div class="text-subtitle-1">
    Datos del cliente
  </div>
  <v-row>
    <v-col cols="12" md="8">
      <v-text-field
        v-model="clientName"
        outlined
        dense
        label="Razón Social *"
        clearable
      />
    </v-col>
    <v-col cols="12" md="4">
      <v-text-field
        v-model="clientRfc"
        outlined
        dense
        label="RFC *"
        maxlength="13"
        :rules="rfcRules"
        @input="clientRfc = (clientRfc || '').toUpperCase()"
        clearable
        @change="onClientRfcChange"
      />
    </v-col>
  </v-row>
  <v-row>
    <v-col cols="12" md="4">
      <v-text-field
        v-model="clientCp"
        outlined
        dense
        label="C.P. Fiscal *"
        maxlength="5"
        inputmode="numeric"
        @keypress="onlyNumberKeypress"
        @paste.prevent="onCpPaste"
        :rules="cpRules"
        clearable
      />
    </v-col>
    <v-col cols="12" md="4">
      <v-autocomplete
        v-model="clientRegimeId"
        :items="fiscalRegimes"
        :loading="loadingFiscalRegimes"
        item-text="label"
        item-value="value"
        outlined
        dense
        clearable
        label="Régimen Fiscal *"
        hide-no-data
      />
    </v-col>
    <v-col cols="12" md="4">
      <v-autocomplete
        v-model="clientUsoCfdi"
        :items="usoCfdiItems"
        :loading="loadingUsoCfdi"
        item-text="display"
        return-object
        outlined
        dense
        label="Uso CFDI *"
      />
    </v-col>
  </v-row>
  <div class="text-subtitle-1">
    Condiciones de pago
  </div>
  <v-row>
    <v-col cols="12" md="6">
      <v-autocomplete
      v-model="clientPaymentType"
      :items="metodoPagos"
      item-text="display"
      return-object
      outlined
      dense
      label="Forma de pago *"
      :disabled="lockSatPaymentFields"
      />
    </v-col>
    <v-col cols="12" md="6">
      <v-autocomplete
      v-model="clientPaymentMethod"
      :items="formaPagos"
      item-text="display"
      return-object
      outlined
      dense
      label="Metodo de pago *"
      :disabled="lockSatPaymentFields"
      />
    </v-col>
  </v-row>
  <v-alert
    v-if="lockSatPaymentFields"
    type="info"
    dense
    outlined
  >
    Por corte de fin de mes en sábado con pago en tarjeta, se asigna
    automáticamente:
    Forma de pago 99 (Por definir) y Método de pago PPD.
  </v-alert>
</div>
</template>
